import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Timer, ArrowLeft, ArrowRight, Check, X, Flag, AlertCircle,
  Trophy, RotateCcw, ChevronRight, Sparkles,
} from "lucide-react";
import { getMockExam, submitMockExam } from "@/lib/api";
import { toast } from "sonner";

function fmtTime(secs) {
  if (secs <= 0) return "00:00";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function Preparing({ progress, total, subject }) {
  const pct = total ? Math.round((progress / total) * 100) : 0;
  const messages = [
    "Sampling questions across every topic…",
    "Asking the AI tutor to mark each answer key…",
    "Mixing in some tricky distractors…",
    "Almost there, sharpening pencils…",
  ];
  const msg = messages[Math.min(messages.length - 1, Math.floor((progress / Math.max(1, total)) * messages.length))];
  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="exam-preparing">
      <div className="max-w-2xl mx-auto p-6 sm:p-10 pt-24">
        <div className="nb-card p-8 sm:p-10 text-center">
          <motion.div
            animate={{ rotate: [0, 6, -6, 0] }}
            transition={{ repeat: Infinity, duration: 1.4 }}
            className="w-16 h-16 mx-auto mb-5 nb-border rounded-2xl bg-[#FFB38E] dark:bg-[#F6AD55] flex items-center justify-center"
          >
            <Sparkles className="w-8 h-8 text-black" strokeWidth={3} />
          </motion.div>
          <div className="label-caps mb-2">{subject || "Mock exam"}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
            Building your exam…
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-6">{msg}</p>

          <div
            className="h-4 nb-border rounded-full overflow-hidden bg-white dark:bg-zinc-900 mb-3"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <motion.div
              className="h-full bg-[#9DFFB0] dark:bg-[#68D391]"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ type: "spring", stiffness: 80, damping: 18 }}
              data-testid="exam-prepare-progress"
            />
          </div>
          <div className="flex items-center justify-between text-sm font-bold">
            <span className="text-zinc-500">
              <span data-testid="exam-prepare-ready">{progress}</span>
              {" / "}
              <span data-testid="exam-prepare-total">{total}</span>
              {" topics ready"}
            </span>
            <span data-testid="exam-prepare-pct">{pct}%</span>
          </div>
          <p className="text-xs font-medium text-zinc-500 mt-6">
            First-time prep can take 10–30s. The countdown timer only starts once everything&apos;s ready.
          </p>
        </div>
      </div>
    </div>
  );
}

function Result({ result, onRetake }) {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl mx-auto p-6 sm:p-10" data-testid="exam-result">
      <div className="nb-card p-8 text-center mb-6">
        <Trophy className="w-12 h-12 mx-auto mb-3" />
        <div className="label-caps mb-2">{result.subject} · Mock exam</div>
        <div className="font-display text-7xl font-black tracking-tighter mb-2" data-testid="exam-score-pct">{result.score_pct}%</div>
        <div className="text-zinc-600 dark:text-zinc-400 font-bold">
          {result.correct} / {result.total} correct · {Math.round((result.seconds_taken || 0) / 60)} min taken
        </div>
      </div>

      {result.per_topic?.length > 0 && (
        <div className="nb-card p-6 mb-6">
          <div className="label-caps mb-4">By topic</div>
          <div className="space-y-3">
            {result.per_topic.map((t) => (
              <div key={t.topic_id} data-testid={`exam-topic-result-${t.topic_id}`}>
                <div className="flex justify-between font-bold text-sm mb-1">
                  <span className="truncate">{t.topic_name}</span>
                  <span className={t.pct >= 80 ? "text-green-600" : t.pct >= 60 ? "text-yellow-600" : "text-orange-600"}>
                    {t.pct}% ({t.correct}/{t.total})
                  </span>
                </div>
                <div className="h-2 nb-border rounded-full overflow-hidden bg-zinc-100 dark:bg-zinc-800">
                  <div
                    className={`h-full ${t.pct >= 80 ? "bg-[#9DFFB0]" : t.pct >= 60 ? "bg-[#FFE066]" : "bg-[#FFB38E]"}`}
                    style={{ width: `${t.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="nb-card p-6 mb-6">
        <div className="label-caps mb-4">Review</div>
        <div className="space-y-3">
          {(result.review || []).map((r, i) => {
            const chosen = r.chosen ?? -1;
            const ok = chosen === r.correct_index;
            return (
              <div
                key={i}
                className={`nb-border rounded-xl p-4 ${ok ? "bg-[#9DFFB0] dark:bg-[#68D391]" : "bg-[#FFB38E] dark:bg-[#F6AD55]"} text-black`}
                data-testid={`exam-review-${i}`}
              >
                <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider">
                  {ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  {r.topic_name}
                </div>
                <div className="font-bold mb-2">{i + 1}. {r.question}</div>
                {!ok && chosen >= 0 && (
                  <div className="text-sm font-medium mb-1">You chose: <b>{r.options?.[chosen]}</b></div>
                )}
                <div className="text-sm font-medium">Correct: <b>{r.options?.[r.correct_index]}</b></div>
                {r.explanation && <div className="text-xs mt-2 opacity-80">{r.explanation}</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <button onClick={onRetake} data-testid="exam-retake-btn" className="nb-button px-5 py-3 bg-[#FFB38E] dark:bg-[#F6AD55] flex items-center gap-2">
          <RotateCcw className="w-4 h-4" /> New mock exam
        </button>
        <button onClick={() => navigate("/curriculum")} className="nb-button px-5 py-3 flex items-center gap-2">
          Back to curriculum <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function MockExamRun() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [prep, setPrep] = useState(null);   // {topics_ready, total_topics, subject} while preparing
  const [answers, setAnswers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [now, setNow] = useState(Date.now());
  const submittedRef = useRef(false);

  // Load exam, poll while preparing
  useEffect(() => {
    let stopped = false;
    let timer;

    const tick = async () => {
      try {
        const e = await getMockExam(examId);
        if (stopped) return;
        if (e.status === "submitted") {
          setResult(e);
          return;
        }
        if (e.status === "preparing") {
          setPrep({
            topics_ready: e.topics_ready ?? 0,
            total_topics: e.total_topics ?? 0,
            subject: e.subject,
          });
          timer = setTimeout(tick, 1200);
          return;
        }
        // in_progress
        setPrep(null);
        setExam(e);
        setAnswers((prev) => (prev.length === e.questions.length ? prev : new Array(e.questions.length).fill(-1)));
      } catch (err) {
        if (stopped) return;
        toast.error(err?.response?.data?.detail || "Couldn't load exam");
        navigate("/exam");
      }
    };
    tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [examId, navigate]);

  // 1-second ticker
  useEffect(() => {
    if (!exam || result) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [exam, result]);

  const submit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const r = await submitMockExam(examId, answers);
      setResult(r);
      if (auto) toast.message("Time's up! Auto-submitted.");
    } catch (e) {
      submittedRef.current = false;
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit when timer hits zero
  const secondsLeft = exam
    ? Math.max(0, Math.round((new Date(exam.expires_at).getTime() - now) / 1000))
    : 0;
  useEffect(() => {
    if (exam && !result && secondsLeft <= 0 && !submittedRef.current) {
      submit(true);
    }
  }, [secondsLeft, exam, result]);

  if (result) return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950">
      <Result result={result} onRetake={() => navigate("/exam")} />
    </div>
  );

  if (prep) return (
    <Preparing
      progress={prep.topics_ready}
      total={prep.total_topics}
      subject={prep.subject}
    />
  );

  if (!exam) return <div className="p-10 font-bold">Loading exam…</div>;

  const q = exam.questions[idx];
  const answered = answers.filter((a) => a !== -1).length;
  const lowTime = secondsLeft <= 60;
  const timeColor = lowTime ? "bg-[#FFB38E] dark:bg-[#F6AD55] text-black animate-pulse" : "bg-[#D4C4FB] dark:bg-[#B794F4] text-black";

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="exam-run-page">
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <div className="font-display font-extrabold text-lg sm:text-xl">{exam.subject} mock</div>
          <div className={`nb-border rounded-xl px-4 py-2 font-mono font-extrabold text-lg flex items-center gap-2 ${timeColor}`} data-testid="exam-timer">
            <Timer className="w-5 h-5" />
            {fmtTime(secondsLeft)}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 sm:p-10 grid lg:grid-cols-[1fr_280px] gap-6">
        {/* Question */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="label-caps">Question {idx + 1} of {exam.questions.length}</div>
            <div className="text-xs font-bold text-zinc-500 truncate ml-3">{q.topic_name}</div>
          </div>
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.18 }}
            className="nb-card p-6 sm:p-8 mb-5"
            data-testid={`exam-question-${idx}`}
          >
            <div className="font-display text-xl sm:text-2xl font-extrabold mb-5">{q.question}</div>
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <button
                  key={oi}
                  data-testid={`exam-option-${idx}-${oi}`}
                  onClick={() => setAnswers((a) => { const c = [...a]; c[idx] = oi; return c; })}
                  className={`nb-border rounded-xl w-full p-4 text-left font-bold transition-all flex items-center gap-3 ${
                    answers[idx] === oi
                      ? "bg-[#D4C4FB] dark:bg-[#B794F4] nb-shadow text-black"
                      : "bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  }`}
                >
                  <span className="w-8 h-8 rounded-full nb-border flex items-center justify-center font-display font-black text-sm bg-white text-black">
                    {String.fromCharCode(65 + oi)}
                  </span>
                  <span className="flex-1">{opt}</span>
                </button>
              ))}
            </div>
          </motion.div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              data-testid="exam-prev-btn"
              className="nb-button px-4 py-3 flex items-center gap-2 disabled:opacity-40"
            >
              <ArrowLeft className="w-4 h-4" /> Prev
            </button>
            {idx < exam.questions.length - 1 ? (
              <button
                onClick={() => setIdx((i) => Math.min(exam.questions.length - 1, i + 1))}
                data-testid="exam-next-btn"
                className="nb-button px-4 py-3 flex items-center gap-2 bg-[#D4C4FB] dark:bg-[#B794F4] text-black"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => submit(false)}
                disabled={submitting}
                data-testid="exam-submit-btn"
                className="nb-button px-5 py-3 flex items-center gap-2 bg-[#9DFFB0] dark:bg-[#68D391] text-black"
              >
                <Flag className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit exam"}
              </button>
            )}
          </div>
        </div>

        {/* Answer sheet sidebar */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="nb-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps">Answer sheet</div>
              <div className="text-xs font-bold text-zinc-500">{answered}/{exam.questions.length}</div>
            </div>
            <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-5 gap-2 mb-4">
              {exam.questions.map((_, i) => {
                const isActive = i === idx;
                const isAns = answers[i] !== -1;
                return (
                  <button
                    key={i}
                    data-testid={`exam-jump-${i}`}
                    onClick={() => setIdx(i)}
                    className={`nb-border rounded-lg aspect-square font-black text-sm flex items-center justify-center ${
                      isActive ? "bg-black text-white dark:bg-white dark:text-black nb-shadow"
                      : isAns ? "bg-[#9DFFB0] dark:bg-[#68D391] text-black"
                      : "bg-white dark:bg-zinc-900"
                    }`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            {answered < exam.questions.length && (
              <div className="nb-border rounded-xl p-3 bg-[#FFE066] dark:bg-[#F6E05E] text-black text-xs font-bold flex items-start gap-2 mb-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>{exam.questions.length - answered} unanswered. They&apos;ll be marked wrong.</div>
              </div>
            )}
            <button
              onClick={() => submit(false)}
              disabled={submitting}
              data-testid="exam-submit-sidebar-btn"
              className="nb-button w-full py-3 bg-[#9DFFB0] dark:bg-[#68D391] text-black flex items-center justify-center gap-2"
            >
              <Flag className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit now"}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
